// src/components/SearchBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import './SearchBar.css';

// ✅ MOVE OUTSIDE: This prevents the array from being re-created on every render
const placeholders = [
  "Scan archives for 'safety protocols'...",
  "Find mentions of 'math placement' changes...",
  "Search for 'ethnic studies' curriculum..."
];

export default function SearchBar({
  onSearch,
  onTermChange,
  isSearching = false,
}) {
  const [term, setTerm] = useState('');
  const [scope, setScope] = useState('all');
  
  // NEW: State for Date Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const inputRef = useRef(null);

  // --- ROTATING PLACEHOLDER LOGIC ---
  const [placeholder, setPlaceholder] = useState("");
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (term.length > 0) {
      setPlaceholder("Search archives...");
      return;
    }

    if (subIndex === placeholders[index].length + 1 && !reverse) {
      const pauseTimeout = setTimeout(() => setReverse(true), 2500);
      return () => clearTimeout(pauseTimeout);
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % placeholders.length);
      return;
    }

    const timeout = setTimeout(() => {
      setPlaceholder(placeholders[index].substring(0, subIndex));
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 30 : 75);

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, term]); 
  // ----------------------------------

  // Notify parent of term changes immediately so it can clear stale results
  // (e.g. an old "no results" message from a typo'd intermediate term).
  useEffect(() => {
    if (onTermChange) onTermChange(term);
  }, [term, onTermChange]);

  // Debounced actual search.
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (term.trim()) {
        onSearch(term.trim(), scope, startDate, endDate);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [term, scope, startDate, endDate, onSearch]);

  const handleScopeChange = (e) => {
    setScope(e.target.value);
    // Trigger immediate search if term exists
    if (term.trim()) {
      onSearch(term.trim(), e.target.value, startDate, endDate);
    }
  };

  const handleClear = () => {
    setTerm('');
    // Optional: Clear dates on "Clear X" or keep them? 
    // Usually user expects just the text to clear, so we leave dates alone.
    inputRef.current?.focus();
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="search-container-col">
      <div className="modern-search-bar">
        <div className={`input-wrapper ${isSearching ? 'loading' : ''}`}>
          
          {/* Scope Selector */}
          <div className="scope-container">
            <select
              value={scope}
              onChange={handleScopeChange}
              className="scope-select"
            >
              <option value="all">All</option>
              <option value="meetings">Meetings</option>
              <option value="topics">Transcripts</option>
            </select>
            <svg className="chevron-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          <div className="divider"></div>

          {/* NEW: Filter Toggle Button */}
          <button 
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={toggleFilters}
            title="Filter by date"
            aria-label="Toggle date filters"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>
          
          <div className="divider"></div>

          {/* Search/Spinner Icon */}
          <div className="icon-container">
            {isSearching ? (
              <svg className="spinner-icon" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            )}
          </div>

          <input
            ref={inputRef}
            type="text"
            value={term}
            autoFocus
            placeholder={placeholder}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              // Enter immediately re-runs the search even if the term hasn't
              // changed, so users can recover after a stale empty-results state
              // (e.g. clicking "Clear search and browse all").
              if (e.key === 'Enter' && term.trim()) {
                e.preventDefault();
                onSearch(term.trim(), scope, startDate, endDate);
              }
            }}
          />

          {term.length > 0 && (
            <button className="clear-btn" onClick={handleClear} aria-label="Clear search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* NEW: Collapsible Date Filters Row */}
      {showFilters && (
        <div className="filters-row">
          <div className="date-group">
            <label>From:</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
            />
          </div>
          <div className="date-group">
            <label>To:</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)} 
            />
          </div>
        </div>
      )}
    </div>
  );
}